import { CategoryFilterViewport } from '@/composables/category/CategoryFilterViewport';
import { restoreCategorySearchScrollPosition } from '@/composables/category/restoreCategorySearchScrollPosition';
import { useCategorySearchPanels } from '@/composables/category/useCategorySearchPanels';
import { useCategorySearchRouting } from '@/composables/category/useCategorySearchRouting';
import { hitSelectItemPayload, useSelectItemTracking } from '@/composables/category/useSelectItemTracking';
import { sendAlgoliaClickEvent } from '@/lib/algoliaInsights';
import { buildVariantValueKey, VARIANT_FILTER_PREFIX } from '@/lib/products/urlFilters';
import { categoryPageStore } from '@/stores/category/CategoryPageStore';
import { categoryProductStore } from '@/stores/category/CategoryProductStore';
import { categorySearchStore } from '@/stores/category/CategorySearchStore';
import { useAlgoliaHeaderSearchStore } from '@/stores/search/AlgoliaHeaderSearchStore';
import type { CategorySearchEventSender, CategorySearchResult, CategorySearchVariant } from '@/types/category/CategorySearchTypes';
import type { CategoryFilterObject } from '@/types/generated';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import _ from 'lodash';
import { computed, onMounted, ref, toValue } from 'vue';

const INSTANT_SEARCH_FUTURE = {
    preserveSharedStateOnUnmount: true,
} as const;
const CATEGORY_SEARCH_TITLE_ID = 'category-search-title';

// In standard Tailwind: lg = 1024px (64rem), 2xl = 1536px (96rem)
const { lg: largeBreakpoint, '2xl': widestBreakpoint } = breakpointsTailwind;

export const categoryProductImageSizes = `(max-width: ${largeBreakpoint}px) calc(50vw - 40px), (max-width: ${widestBreakpoint}px) calc(25vw - 24px), 360px`;

const getConfigureSearchParameters = (filters: string, hitsPerPage: number, clickAnalytics: boolean) => ({
    clickAnalytics,
    facets: ['*'],
    filters,
    hitsPerPage,
});

const escapeSearchFilterValue = (value: string) => value.replaceAll('"', '\\"');

export function useCategorySearch() {
    const searchStore = categorySearchStore();
    const pageStore = categoryPageStore();
    const productStore = categoryProductStore();

    const categoryUrlKey = computed(() => pageStore.category.urlKey);
    // Do not drop categories.name: Algolia query rules match on it, so without it the
    // category loses its merchandising, facet ordering and hand-pinned positions.
    const categorySearchFilters = computed(() => {
        const { joostId, title: categoryTitle } = pageStore.category;
        const escapedName = escapeSearchFilterValue(categoryTitle);

        return `categories.id:${joostId} AND categories.name:"${escapedName}"`;
    });
    const title = computed(() => pageStore.category.title);
    const description = computed(() => pageStore.category.description);
    const faqs = computed(() => pageStore.category.faqs ?? []);
    const categoryLinks = computed(() => pageStore.category.categoryLinks);
    const categoryBanners = computed(() => pageStore.category.categoryBanners);
    const hideCategoryLinksOnDesktop = computed(() => pageStore.categoryListerPage?.hide_category_links_on_desktop ?? false);
    const hideCategoryLinksOnMobile = computed(() => pageStore.categoryListerPage?.hide_category_links_on_mobile ?? false);
    const expandDescriptionOnDesktop = computed(() => pageStore.categoryListerPage?.expand_description_on_desktop ?? false);
    const expandDescriptionOnMobile = computed(() => pageStore.categoryListerPage?.expand_description_on_mobile ?? false);

    const hitsPerPage = ref(pageStore.defaultHitsPerPage);
    const isMobileFiltersOpen = ref(false);

    const searchClient = computed(() => searchStore.searchClient);
    const insightsClient = searchStore.insightsClient;
    const productIndex = computed(() => productStore.productIndex);
    const categoryFilters = computed<CategoryFilterObject[]>(() => Object.values(pageStore.categoryListerPage?.category_filters ?? {}));
    const { visibleFilterPanels, desktopFilterPanels, mobileFilterPanels, headerFilterPanels } = useCategorySearchPanels(
        categoryFilters,
        productStore.availableFilters,
        pageStore.availableFilters,
        computed(() => pageStore.categoryListerPage?.hide_popular_filters_on_desktop ?? false),
        computed(() => pageStore.categoryListerPage?.hide_popular_filters_on_mobile ?? false),
        computed(() => pageStore.category.settings.header_filters_count ?? 0),
    );

    // SSR renders the `lg` breakpoint's width (see ssrViewportWidthPx), so this stays stable through hydration.
    const isDesktopViewport = useBreakpoints(breakpointsTailwind).greaterOrEqual('lg');
    const activeFilterPanels = computed(() => (isDesktopViewport.value ? desktopFilterPanels.value : mobileFilterPanels.value));
    const activeFilterViewport = computed(() => (isDesktopViewport.value ? CategoryFilterViewport.Desktop : CategoryFilterViewport.Mobile));

    const hasFilterPanels = computed(() => productStore.filtersEnabled && activeFilterPanels.value.length > 0);
    const defaultRefinements = computed((): Record<string, string[]> =>
        categoryFilters.value
            .filter((filter: CategoryFilterObject) => filter.default_values && filter.default_values.length > 0)
            .reduce((refinements: Record<string, string[]>, filter: CategoryFilterObject) => {
                refinements[filter.filter_identifier] = filter.default_values!.map(String);

                return refinements;
            }, {}),
    );

    const { routing, pendingDefaultRefinements, applyPendingDefaultRefinements, clearPendingDefaultRefinements } = useCategorySearchRouting(
        categoryUrlKey,
        productIndex,
        defaultRefinements,
        computed(() => pageStore.availableFilters),
        computed(() => pageStore.initialRefinements),
        computed(() => pageStore.idFilters),
        computed(() => productStore.nieuwReplica),
    );

    const configureSearchParameters = computed(() =>
        getConfigureSearchParameters(toValue(categorySearchFilters), hitsPerPage.value, productStore.insightsEnabled),
    );

    const restoreResultCount = () => {
        const urlKey = toValue(categoryUrlKey);
        if (!urlKey) {
            return;
        }

        const itemIndex = searchStore.getStoredResultIndex(urlKey);
        if (itemIndex === null) {
            return;
        }

        if (itemIndex < hitsPerPage.value) {
            searchStore.clearStoredResultIndex();
            return;
        }

        hitsPerPage.value = Math.max(
            pageStore.defaultHitsPerPage,
            Math.ceil((itemIndex + 1) / pageStore.defaultHitsPerPage) * pageStore.defaultHitsPerPage,
        );
        searchStore.clearStoredResultIndex();
    };

    // Must run in setup: onMounted runs after InstantSearch already fired its first query.
    restoreResultCount();

    const restoreScrollPosition = (): void => {
        restoreCategorySearchScrollPosition({
            scrollY: searchStore.latestResultScrollY,
            onFinish: searchStore.clearStoredResultScrollY,
        });
    };

    const isCarriedFilter = (filter: CategoryFilterObject): boolean =>
        filter.use_as_product_page_filter && _.startsWith(filter.filter_identifier, VARIANT_FILTER_PREFIX);

    const toCarriedFilterParam = (filter: CategoryFilterObject): [string, string] | null => {
        const selectedValue = searchStore.selectedFilters[filter.filter_identifier];

        if (!selectedValue) {
            return null;
        }

        return [_.replace(filter.filter_identifier, VARIANT_FILTER_PREFIX, ''), selectedValue];
    };

    // Carries this lister page's flagged & currently selected variant filters onto product-page links.
    const carriedFilterParams = computed((): Record<string, string> =>
        _.chain(categoryFilters.value).filter(isCarriedFilter).map(toCarriedFilterParam).compact().fromPairs().value(),
    );

    const carriedVariantValueKeys = computed((): string[] => Object.keys(carriedFilterParams.value).map(buildVariantValueKey));

    const resolveVariantForItem = (item: CategorySearchResult): CategorySearchResult => {
        const variantFilters = Object.fromEntries(Object.entries(searchStore.selectedFilters).filter(([key]) => key.includes('variants.')));

        if (Object.keys(variantFilters).length === 0 || !item.variants?.length) {
            return {
                ...item,
                image_sizes: categoryProductImageSizes,
                image_srcset: item.image_srcset ?? undefined,
                previews: item.previews ?? null,
            };
        }

        const matchedVariant = item.variants.find((variant: CategorySearchVariant) =>
            Object.entries(variantFilters).every(([key, value]) => {
                const variantKey = key.split('.').pop()!;
                return String(variant[variantKey]) === value;
            }),
        );

        if (!matchedVariant) {
            return {
                ...item,
                image_sizes: categoryProductImageSizes,
                image_srcset: item.image_srcset ?? undefined,
                previews: item.previews ?? null,
            };
        }

        return {
            ...item,
            // Links carry the attribute's raw value, the lister page facets on its label.
            ..._.pick(matchedVariant, carriedVariantValueKeys.value),
            selected_sku: matchedVariant.sku ?? item.sku ?? null,
            image_url: matchedVariant.image_url ?? item.image_url,
            image_sizes: categoryProductImageSizes,
            // A variant's own image must not borrow the parent's srcset, or the browser picks the parent photo.
            image_srcset: matchedVariant.image_url ? matchedVariant.image_srcset : item.image_srcset,
            previews: matchedVariant.previews ?? null,
            price: matchedVariant.price ?? item.price,
            ...resolveVariantDiscount(matchedVariant, item),
        };
    };

    // A variant without its own recommended price (e.g. a sample) is not discounted, so it must not inherit the hit's.
    const resolveVariantDiscount = (
        variant: CategorySearchVariant,
        item: CategorySearchResult,
    ): Pick<CategorySearchResult, 'recommended_price' | 'discount_percentage'> => {
        if (variant.recommended_price === undefined || variant.recommended_price === null) {
            return { recommended_price: null, discount_percentage: null };
        }

        return {
            recommended_price: variant.recommended_price,
            discount_percentage: 'discount_percentage' in variant ? (variant.discount_percentage ?? null) : item.discount_percentage,
        };
    };

    const asCategorySearchResult = (item: CategorySearchResult): CategorySearchResult => resolveVariantForItem(item);

    // The refinements when the drawer opened; confirming without changing them leaves the page where it is.
    let refinementKeyAtOpen: string | undefined = undefined;

    const openMobileFilters = () => {
        refinementKeyAtOpen = searchStore.refinementKey;
        isMobileFiltersOpen.value = true;
    };

    const closeMobileFilters = () => {
        isMobileFiltersOpen.value = false;
    };

    const scrollToCategoryTitle = (behavior: ScrollBehavior = 'smooth'): void => {
        if (typeof document === 'undefined') {
            return;
        }

        const titleElement = document.getElementById(CATEGORY_SEARCH_TITLE_ID);
        if (titleElement === null) {
            return;
        }

        // The title's scroll margin matches the page padding, so nothing above it peeks in.
        titleElement.scrollIntoView({ behavior, block: 'start' });
    };

    const handleDesktopFilterApplied = (): void => {
        window.setTimeout(scrollToCategoryTitle, 50);
    };

    // On mobile the closing drawer still covers the page, so it jumps there in the first frame; on desktop the page is in view and glides.
    const handleMobileFilterApplied = (): void => {
        closeMobileFilters();

        if (searchStore.refinementKey === refinementKeyAtOpen) {
            return;
        }

        requestAnimationFrame(() => scrollToCategoryTitle(isDesktopViewport.value ? 'smooth' : 'instant'));
    };

    const loadMoreResults = () => {
        hitsPerPage.value += pageStore.defaultHitsPerPage;
    };

    const { trackSelectItem } = useSelectItemTracking();

    const sendClickEvent = (item: CategorySearchResult, sendEvent?: CategorySearchEventSender): void =>
        sendAlgoliaClickEvent(item, productStore.insightsEnabled, toValue(productIndex), insightsClient, sendEvent);

    const handleResultClick = (index: number, item: CategorySearchResult, sendEvent?: CategorySearchEventSender) => {
        const urlKey = toValue(categoryUrlKey);
        if (urlKey) {
            searchStore.setStoredResultIndex(urlKey, index);
        }

        searchStore.setStoredResultScrollY(window.scrollY);

        if (productStore.insightsEnabled && item.__queryID) {
            const headerSearchStore = useAlgoliaHeaderSearchStore();

            searchStore.setLatestQueryId(item.__queryID);
            headerSearchStore.latestQueryId = item.__queryID;
            headerSearchStore.latestQueryIndex = productStore.queriedIndex ? { queryId: item.__queryID, index: productStore.queriedIndex } : null;
        }

        trackSelectItem(index, hitSelectItemPayload(asCategorySearchResult(item)));

        sendClickEvent(item, sendEvent);
    };

    onMounted(() => {
        restoreScrollPosition();
    });

    return {
        collectionFilterAttribute: pageStore.collectionFilterAttribute,
        categoryTitleId: CATEGORY_SEARCH_TITLE_ID,
        defaultFacetValueLimit: pageStore.defaultFacetValueLimit,
        defaultHitsPerPage: pageStore.defaultHitsPerPage,
        defaultShowMoreLimit: pageStore.defaultShowMoreLimit,
        instantSearchFuture: INSTANT_SEARCH_FUTURE,
        routing: routing.value,
        categoryBanners,
        categoryFilters,
        categoryLinks,
        configureSearchParameters,
        carriedFilterParams,
        description,
        expandDescriptionOnDesktop,
        expandDescriptionOnMobile,
        faqs,
        hideCategoryLinksOnDesktop,
        hideCategoryLinksOnMobile,
        title,
        handleDesktopFilterApplied,
        handleMobileFilterApplied,
        handleResultClick,
        hasFilterPanels,
        isDesktopViewport,
        isMobileFiltersOpen,
        loadMoreResults,
        activeFilterPanels,
        activeFilterViewport,
        mobileFilterPanels,
        openMobileFilters,
        closeMobileFilters,
        productIndex,
        productStore,
        searchClient,
        insightsClient,
        visibleFilterPanels,
        desktopFilterPanels,
        headerFilterPanels,
        asCategorySearchResult,
        pendingDefaultRefinements,
        applyPendingDefaultRefinements,
        clearPendingDefaultRefinements,
    };
}
